import { AnalyzeWeekButton } from './AnalyzeButtons';
import { TimePeriod } from '@/utils/prismaEnums';
import type { TimelyAnalysis } from '@trainme/db/client';
import type { Week } from '@/app/calendar/InfiniteCalendar';
import { WeeklyScoreBar } from '@/app/calendar/WeeklyScoreBar';
import { startOfDay } from 'date-fns';
import { trpc } from '@/app/api/trpc/client';
import { useCallback, useMemo } from 'react';
import { useTimelyAnalysisStore } from '@/app/stores/timelyAnalysisStore';

interface CalendarWeekAnalysisProps {
  week: Week;
  activitiesCount?: number;
  workoutsCount?: number;
}

export const CalendarWeekAnalysis = ({
  week,
  activitiesCount,
  workoutsCount,
}: CalendarWeekAnalysisProps) => {
  const normalizedStartDate = useMemo(
    () => startOfDay(week.start),
    [week.start]
  );
  const utils = trpc.useUtils();
  const mergeTimelyAnalyses = useTimelyAnalysisStore(
    (s) => s.mergeTimelyAnalyses
  );
  const weeklyAnalysis = useTimelyAnalysisStore((s) =>
    s.getTimelyAnalysis(TimePeriod.WEEKLY, normalizedStartDate)
  );

  const refreshWeeklyAnalysis = useCallback(async () => {
    const analysis = await utils.analysis.getTimelyAnalysis.fetch({
      period: TimePeriod.WEEKLY,
      startDate: normalizedStartDate,
    });
    if (analysis) {
      mergeTimelyAnalyses([analysis]);
    }
  }, [mergeTimelyAnalyses, normalizedStartDate, utils]);

  const handleSuccess = useCallback(async () => {
    await utils.analysis.getTimelyAnalysis.invalidate({
      period: TimePeriod.WEEKLY,
      startDate: normalizedStartDate,
    });
    await refreshWeeklyAnalysis();
  }, [normalizedStartDate, refreshWeeklyAnalysis, utils]);

  const hasAnalysis =
    !!weeklyAnalysis &&
    weeklyAnalysis.trainmeScore !== null &&
    weeklyAnalysis.trainmeScore !== undefined;

  return (
    <div className='flex items-center gap-1 flex-1 justify-end min-w-0'>
      {hasAnalysis && weeklyAnalysis && (
        <WeeklyScoreBar
          week={week}
          analysis={weeklyAnalysis as TimelyAnalysis}
        />
      )}
      <AnalyzeWeekButton
        weekStart={week.start}
        activitiesCount={activitiesCount}
        workoutsCount={workoutsCount}
        onSuccess={handleSuccess}
      />
    </div>
  );
};
